import React, { useState, useEffect, useContext, useCallback } from 'react';
import { FirebaseContext } from '../App'; // Import FirebaseContext
import { getVideos } from '../utils/firestoreService'; // Import Firestore service
import VideoCard from './VideoCard'; // Import VideoCard component
import './VideoList.css';

function VideoList({ searchQuery, selectedCategory, userId, currentUserXUsername }) {
    // Access db and isAuthReady from FirebaseContext
    const { db, isAuthReady } = useContext(FirebaseContext);

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Function to fetch videos, memoized with useCallback
    const fetchVideos = useCallback(async () => {
        if (!db || !isAuthReady) {
            // console.log("DB not ready or Auth not ready, skipping fetch.");
            return;
        }

        setLoading(true);
        setError('');
        try {
            // Fetch videos using the service function with current search and category
            const fetchedVideos = await getVideos(db, searchQuery, selectedCategory);
            setVideos(fetchedVideos);
        } catch (err) {
            console.error("Error fetching videos:", err);
            setError("Failed to load videos. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [db, isAuthReady, searchQuery, selectedCategory]); // Dependencies for useCallback

    // Effect to fetch videos whenever search query, category, or Firebase state changes
    useEffect(() => {
        fetchVideos();
    }, [fetchVideos]); // Depend on the memoized fetchVideos function

    // Handler for when a video is successfully deleted from a VideoCard
    const handleVideoDelete = useCallback((deletedVideoId) => {
        // Optimistically remove the video from the state
        setVideos(prevVideos => prevVideos.filter(video => video.id !== deletedVideoId));
    }, []);

    if (loading) {
        return <p className="loading-message">Loading game edits...</p>;
    }

    if (error) {
        return <p className="error-message">{error}</p>;
    }

    return (
        <div className="video-list-container">
            {videos.length === 0 ? (
                <p className="no-videos-message">No game edits found matching your criteria.</p>
            ) : (
                <div className="video-grid">
                    {videos.map((video) => (
                        <VideoCard
                            key={video.id}
                            video={video}
                            onDelete={handleVideoDelete}
                            currentUserXUsername={currentUserXUsername} // Pass current user's X username for delete logic
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default VideoList;
